import { useLocation } from 'react-router-dom';

import AiIcon from '@/assets/all-icons/ai-icon';
import useStateHandler from '@/hooks/useStateHandler';
import RoomViewAction from '@/pages/artwork/room-view/RoomViewAction';
import LikeButton from '@/shared-components/buttons/components/LikeButton';
import FillableThoughtIcon from '@/shared-components/icons/components/artwork/FillableThoughtIcon';
import RatingIcon from '@/shared-components/icons/components/artwork/RatingIcon';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';
import { isRestrictedArtwork, shouldHideCollectorInteractions } from '@/utils/artworkVisibility';

const formatNonZeroCount = (value) => {
  return value > 0 && Utils.numberParserMillionThousand(value);
};

// Shared social actions displayed below the artwork information.
export default function ArtworkSocials({
  artworkData,
  socialsData,
  onClickThought,
  onClickRating,
  onClickAskAi,
  onClickLike,
}) {
  const { state } = useStateHandler();
  const location = useLocation();
  const viewerType = state?.user_session?.user_type ?? state?.user_session?.__t ?? null;
  const searchParams = new URLSearchParams(location.search);
  const holdingTypeOverride =
    searchParams.get('holding_type')
    ?? location.state?.artworkViewContext?.holdingType
    ?? null;
  const hideCollectorInteractions = shouldHideCollectorInteractions(
    artworkData,
    viewerType,
    holdingTypeOverride,
  );
  const forceHide =
    !viewerType
    && isRestrictedArtwork(artworkData)
    && (holdingTypeOverride || '').toLowerCase() === 'limited_edition';
  if (hideCollectorInteractions || forceHide) {
    return null;
  }
  const ratingsCount = Number(socialsData?.ratings_count) || 0;
  const thoughtsCount = Number(socialsData?.thoughts_count) || 0;
  const likesCount = Number(socialsData?.likes_count) || 0;
  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const showAskAi = typeof onClickAskAi === 'function';

  return (
    <div
      className="artwork-label-socials__container element-non-selectable"
    >
      <div
        className="artwork-label-socials__item"
        onClick={(event) => {
          event.stopPropagation();
          onClickRating();
        }}
      >
        <RatingIcon className={`artwork-label-socials__item-icon-rating ${socialsData?.published_rating ? 'active' : ''}`} />

        <Text className="artwork-label-socials__item-count">
          {formatNonZeroCount(ratingsCount)}
        </Text>
      </div>

      {!isAuctionArtwork && (
        <div
          className="artwork-label-socials__item"
          onClick={(event) => {
            event.stopPropagation();
            onClickThought();
          }}
        >
          <FillableThoughtIcon className={`artwork-label-socials__item-icon-thought ${socialsData?.written_thought ? 'active' : ''}`} />

          <Text className="artwork-label-socials__item-count">
            {formatNonZeroCount(thoughtsCount)}
          </Text>
        </div>
      )}

      {showAskAi && (
        <div
          className="artwork-label-socials__item artwork-label-socials__item--ai"
          onClick={(event) => {
            event.stopPropagation();
            onClickAskAi();
          }}
        >
          <AiIcon className="artwork-label-socials__item-icon-ai" />
          <Text className="artwork-label-socials__item-count artwork-label-socials__item-count--empty">
            {' '}
          </Text>
        </div>
      )}

      <RoomViewAction artworkData={artworkData} holdingTypeOverride={holdingTypeOverride} />

      <div className="artwork-label-socials__item">
        <LikeButton
          itemId={artworkData._id}
          typeOfItem="ARTWORK"
          isLiked={socialsData?.is_liked}
          className="artwork-label-socials__item-icon-like"
          onLikeAction={onClickLike}
        />

        <Text className="artwork-label-socials__item-count">
          {formatNonZeroCount(likesCount)}
        </Text>
      </div>
    </div>
  );
}
